import { useState, useCallback, useRef, useEffect } from 'react'
import { SceneObject, dbService } from '../db/service'

interface HistoryEntry {
  objects: SceneObject[]
  action: string
  timestamp: string
}

export function useUndoRedo(initialObjects: SceneObject[] = []) {
  const [objects, setObjects] = useState<SceneObject[]>(initialObjects)
  const [ready, setReady] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([{
    objects: initialObjects,
    action: 'Initial state',
    timestamp: new Date().toISOString()
  }])
  const [currentIndex, setCurrentIndex] = useState(0)
  const isUndoRedo = useRef(false)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingSaveRef = useRef<{ objects: SceneObject[], action: string } | null>(null)

  // Debounced save to database and history
  const debouncedSave = useCallback((newObjects: SceneObject[], action: string) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Store the pending save
    pendingSaveRef.current = { objects: newObjects, action }

    // Set a new timeout
    saveTimeoutRef.current = setTimeout(async () => {
      const pending = pendingSaveRef.current
      if (!pending) return

      const newEntry: HistoryEntry = {
        objects: JSON.parse(JSON.stringify(pending.objects)), // Deep clone
        action: pending.action,
        timestamp: new Date().toISOString()
      }

      // Remove any history entries after current index (for new branches)
      const newHistory = history.slice(0, currentIndex + 1)
      newHistory.push(newEntry)

      // Keep only last 50 history entries to prevent memory issues
      const trimmedHistory = newHistory.slice(-50)
      const newIndex = trimmedHistory.length - 1

      setHistory(trimmedHistory)
      setCurrentIndex(newIndex)

      // Save to database if available
      try {
        if (dbService.isAvailable()) {
          await dbService.saveSceneState(pending.objects, pending.action)
          console.log(`State saved: ${pending.action}`)
        } else {
          console.warn('Database not available, state saved only in memory')
        }
      } catch (error) {
        console.error('Failed to save to database:', error)
      }

      pendingSaveRef.current = null
    }, 1000) // 1 second delay
  }, [history, currentIndex])

  // Immediate UI update for smooth interaction
  const updateObjects = useCallback((newObjects: SceneObject[], action?: string) => {
    if (isUndoRedo.current) {
      isUndoRedo.current = false
      return
    }

    // Update UI immediately
    setObjects(newObjects)

    // Queue debounced save if action is provided
    if (action) {
      debouncedSave(newObjects, action)
    }
  }, [debouncedSave])

  // Save state immediately (for explicit saves like adding/deleting objects)
  const saveState = useCallback(async (newObjects: SceneObject[], action: string) => {
    if (isUndoRedo.current) {
      isUndoRedo.current = false
      return
    }

    // Cancel any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }

    const newEntry: HistoryEntry = {
      objects: JSON.parse(JSON.stringify(newObjects)), // Deep clone
      action,
      timestamp: new Date().toISOString()
    }

    // Remove any history entries after current index (for new branches)
    const newHistory = history.slice(0, currentIndex + 1)
    newHistory.push(newEntry)

    // Keep only last 50 history entries to prevent memory issues
    const trimmedHistory = newHistory.slice(-50)
    const newIndex = trimmedHistory.length - 1

    setHistory(trimmedHistory)
    setCurrentIndex(newIndex)
    setObjects(newObjects)

    // Save to database immediately
    try {
      if (dbService.isAvailable()) {
      await dbService.saveSceneState(newObjects, action)
        console.log(`State saved immediately: ${action}`)
      } else {
        console.warn('Database not available, state saved only in memory')
      }
    } catch (error) {
      console.error('Failed to save to database:', error)
    }
  }, [history, currentIndex])

  // Undo function
  const undo = useCallback(() => {
    if (currentIndex > 0) {
      // Cancel any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }

      isUndoRedo.current = true
      const newIndex = currentIndex - 1
      const previousState = history[newIndex]
      
      setCurrentIndex(newIndex)
      setObjects(previousState.objects)
      
      console.log(`Undoing: ${history[currentIndex].action} → ${previousState.action}`)
    }
  }, [currentIndex, history])

  // Redo function
  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      // Cancel any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }

      isUndoRedo.current = true
      const newIndex = currentIndex + 1
      const nextState = history[newIndex]
      
      setCurrentIndex(newIndex)
      setObjects(nextState.objects)
      
      console.log(`Redoing: ${history[currentIndex].action} → ${nextState.action}`)
    }
  }, [currentIndex, history])

  // Load state from database on mount
  const loadFromDatabase = useCallback(async () => {
    try {
      // Ensure database is initialized before loading
      await dbService.init()
      
      if (dbService.isAvailable()) {
      const loadedObjects = await dbService.loadSceneState()
      if (loadedObjects.length > 0) {
          // Ensure all objects have required hierarchy fields
          const migratedObjects = loadedObjects.map((obj, index) => ({
            ...obj,
            name: obj.name || `${obj.type.charAt(0).toUpperCase() + obj.type.slice(1)} ${index + 1}`,
            parentId: obj.parentId || null,
            children: obj.children || [],
            order: obj.order != null ? obj.order : index
          }))
          
          setObjects(migratedObjects)
        setHistory([{
            objects: migratedObjects,
          action: 'Loaded from database',
          timestamp: new Date().toISOString()
        }])
        setCurrentIndex(0)
          console.log('Successfully loaded scene from database with', migratedObjects.length, 'objects')
          return true
        } else {
          console.log('No saved scene found in database')
          return false
        }
      } else {
        console.warn('Database not available for loading')
        return false
      }
    } catch (error) {
      console.error('Failed to load from database:', error)
      return false
    } finally {
      setReady(true)
    }
  }, [])

  // Auto-load on mount
  useEffect(() => {
    loadFromDatabase()
  }, [loadFromDatabase])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  const canUndo = currentIndex > 0
  const canRedo = currentIndex < history.length - 1

  return {
    objects,
    ready,
    saveState, // For explicit saves (add/delete operations)
    updateObjects, // For smooth UI updates with debounced saves
    undo,
    redo,
    canUndo,
    canRedo,
    loadFromDatabase,
    currentAction: history[currentIndex]?.action || 'Unknown',
    historyLength: history.length,
    isDatabaseAvailable: dbService.isAvailable()
  }
}
